import { Component, EventEmitter, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { JsonModalResult } from '@src/app/components/modals/json-modal/json-modal.component';
import { JsonModalDataObject } from '@src/app/model/json-modal-data-object';
import { takeWhile } from 'rxjs';
import { ToJsonInputTranslatable } from '@src/app/model/to-json-input-translatable';
import { ReverseJsonInputTranslatable } from '@src/app/model/reverse-json-input-translatable';
import { v4 as uuidv4 } from 'uuid';
import { RuleData } from '@src/app/components/modals/rule-editor-modal/rule-editor-modal.utils';
import { cloneDeep } from 'lodash';
import { JsonModal } from '@src/app/components/utils/json-modal';
import { CombinedGraphQLErrors } from '@apollo/client';

@Component({
  selector: 'app-rule-editor-modal',
  templateUrl: './rule-editor-modal.component.html',
  styleUrls: ['./rule-editor-modal.component.scss'],
  standalone: false,
})
export class RuleEditorModalComponent implements OnInit {
  public ruleTypeText: string = '';
  public ruleList: RuleData[];
  public crud: boolean;
  public readOnly: boolean;
  public additionalFieldsTemplate: ToJsonInputTranslatable;
  public selectedRuleIndex: number = 0;
  public errorMessage: string = undefined;
  public baseTranslationKey: string = '';
  public showAdditional: boolean = false;
  public options: object;

  public submit: EventEmitter<RuleData[]> = new EventEmitter<RuleData[]>();

  private _subOpen: boolean;

  constructor(
    private _dialogRef: MatDialogRef<RuleEditorModalComponent>,
    private _dialog: MatDialog,
    private _translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) private _data: {
      ruleTypeText: string,
      ruleList: RuleData[],
      additionalFieldsTemplate?: ToJsonInputTranslatable, // Empty version of the additional fields for adding new rules
      baseTranslationKey: string,
      crud?: boolean,
      readOnly?: boolean,
      showAdditional?: boolean,
    }
  ) {
  }

  public ngOnInit(): void {
    this.ruleTypeText = this._data.ruleTypeText;
    // we do a clonedeep here since we DO NOT want to modify the original rule objects
    // (this would cause issues with unsaved changes persisting on dialog close)
    this.ruleList = cloneDeep(this._data.ruleList);
    this.additionalFieldsTemplate = this._data.additionalFieldsTemplate;
    this.crud = this._data.crud ?? false;
    this.readOnly = this._data.readOnly ?? false;
    this.baseTranslationKey = this._data.baseTranslationKey;
    this.showAdditional = this._data.showAdditional ?? true;
    this.options = { language: 'text/plain', readOnly: this.readOnly };
  }

  public closeDialog(): void {
    this._dialogRef.close();
  }

  public changeSelectedRule(selection: number) {
    this.selectedRuleIndex = selection;
  }

  public save(): void {
    this.submit.emit(this.ruleList);
  }

  public openDocs(): void {
    window.open('assets/rule-executor-docs/index.html', '_blank');
  }

  public addRule(): void {
    const newRule = new RuleData(uuidv4(), '', this.additionalFieldsTemplate, '');
    this.ruleList.push(newRule);
  }

  public removeSelectedRule(): void {
    if (this.selectedRuleIndex < this.ruleList.length) {
      this.ruleList.splice(this.selectedRuleIndex, 1);
      if (this.selectedRuleIndex > 0) {
        this.selectedRuleIndex -= 1;
      }
    }
  }

  public showError(err: Error): void {
    if (CombinedGraphQLErrors.is(err)) {
      this.errorMessage = err.errors.reduce((totalErrorMessage, currentError) => {
        let errorMessages;
        try {
          errorMessages = JSON.parse(currentError.extensions.errors as string);
        } catch {
        }
        if (Array.isArray(errorMessages)) {
          errorMessages.forEach(em => totalErrorMessage += `Type: ${em.type}\n\tMessage: ${em.message}\n`);
        } else {
          totalErrorMessage += `Error: ${currentError.extensions.errors}\n`;
        }
        return totalErrorMessage;
      }, '');
    } else {
      this.errorMessage = err.message || this._translateService.instant('updateRules.unknownError');
    }
  }

  public openEditAdditionalValuesDialog($event: any, itemToEditIndex: number): void {
    $event.stopPropagation();

    // If there are rules, and the additionalFields implement ReverseJsonInputTranslatable, you can edit them
    if (
      this.ruleList?.length > 0
      && this.isReverseJsonInputTranslatable(this.ruleList[itemToEditIndex].additionalFields)
    ) {
      const dialogRef = JsonModal.openJSONModalDialog(this._dialog, new JsonModalDataObject(
        this.ruleTypeText,
        this.ruleList[itemToEditIndex].additionalFields,
        `${this.ruleList[itemToEditIndex].ruleId}`,
        'updateRules.submitAdditionalFields',
        ['id', 'rule', 'correction'],
        this.readOnly || false
      ));
      this._subOpen = true;
      dialogRef.componentInstance.submit
        .pipe(takeWhile(() => this._subOpen === true))
        .subscribe({
          next: (jsonEmit: JsonModalResult) => {
            const translatableItem = jsonEmit.body[this.ruleTypeText] as object;
            this.ruleList[itemToEditIndex].additionalFields = (
              this.ruleList[itemToEditIndex].additionalFields as unknown as ReverseJsonInputTranslatable
            ).updateValuesFromGqlInput(translatableItem);
            this._subOpen = false;
            dialogRef.close();
          },
        });
    }
  }

  public isReverseJsonInputTranslatable(object: any): object is ReverseJsonInputTranslatable {
    return 'updateValuesFromGqlInput' in object;
  }
}
